/* MASONTEX · Media Core — normalize.js (flat asset path)
   Pure utilities + config/media normalization. DOM-free.
   Loads before oem-product-lite.js; attaches to globalThis.MasontexMediaCore. */
(function (global) {
  'use strict';

  const api = (global.MasontexMediaCore = global.MasontexMediaCore || {});
  const normalize = {};

  normalize.asArray = (value) => (Array.isArray(value) ? value : []);

  normalize.parseJson = (text, fallback) => {
    try {
      const value = text ? JSON.parse(text) : fallback;
      return value === undefined || value === null ? fallback : value;
    } catch (error) {
      return fallback;
    }
  };

  normalize.collection = (value) => {
    if (Array.isArray(value)) {
      return value.map((item, index) => ({ key: String(item?.key ?? item?.id ?? item?.value ?? index), ...(item || {}) }));
    }
    if (value && typeof value === 'object') {
      return Object.entries(value).map(([itemKey, item]) => ({ key: itemKey, ...(item || {}) }));
    }
    return [];
  };

  normalize.itemKey = (item) => String(item?.key ?? item?.id ?? item?.value ?? '');

  normalize.itemLabel = (item, fallback = '') =>
    String(item?.display ?? item?.label ?? item?.name ?? item?.title ?? fallback ?? normalize.itemKey(item));

  normalize.findItem = (items, value) => items.find((item) => normalize.itemKey(item) === value) || items[0] || {};

  normalize.titleCase = (value) =>
    String(value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  normalize.tokens = (value) =>
    String(value || '')
      .split(/\|\||,/)
      .map((item) => item.trim())
      .filter(Boolean);

  normalize.slug = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  normalize.shortCode = (value, fallback = 'NA') =>
    (String(value || fallback).replace(/[^a-z0-9]/gi, '').toUpperCase() || fallback).slice(0, 3);

  normalize.unique = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const signature = JSON.stringify(item);
      if (seen.has(signature)) return false;
      seen.add(signature);
      return true;
    });
  };

  normalize.extractBase = (entry) => {
    if (typeof entry === 'string') return entry;
    return entry?.base || entry?.image || entry?.url || entry?.src || '';
  };

  normalize.extractOverlay = (entry) => {
    if (!entry || typeof entry !== 'object') return '';
    return entry.overlay || entry.layer || '';
  };

  normalize.sameAsset = (a, b) => {
    /* The same Shopify asset can be served from different CDN hosts/paths
       (custom domain vs cdn.shopify.com) with or without width params.
       Compare by filename + version token only. */
    const key = (url) => {
      const value = String(url || '');
      const file = value.split('/').pop().split('?')[0];
      const version = (value.match(/[?&]v=([^&]+)/) || [])[1] || '';
      return file ? `${file}@${version}` : '';
    };
    const keyA = key(a);
    const keyB = key(b);
    return !!keyA && !!keyB && keyA === keyB;
  };

  normalize.viewLabel = (item, index = 0) => {
    const text = `${item?.label || ''} ${item?.display || ''} ${item?.alt || ''} ${item?.key || ''}`.toLowerCase();
    if (/rear/.test(text) && /quarter|3\/4/.test(text)) return 'REAR 3/4';
    if (/rear|back/.test(text)) return 'REAR';
    if (/side/.test(text)) return 'SIDE';
    if (/front/.test(text)) return 'FRONT';
    if (/quarter|3\/4/.test(text)) return '3/4 VIEW';
    return index ? `VIEW ${index + 1}` : 'MAIN VIEW';
  };

  normalize.translate = (rawValue, displayValues) => {
    const raw = String(rawValue ?? '').trim();
    if (!raw) return raw;
    const exact = displayValues[raw.toUpperCase()];
    if (exact) return exact;
    if (raw.includes('/')) {
      const parts = raw.split(/\s*\/\s*/);
      const mapped = parts.map((part) => displayValues[part.toUpperCase()] || part);
      if (mapped.some((part, index) => part !== parts[index])) return mapped.join(' / ');
    }
    return raw;
  };

  normalize.viewLabelWithI18n = (item, index, displayValues, overviewText) => {
    const raw = normalize.viewLabel(item, index);
    if (/^VIEW \d+$/i.test(raw)) return `${overviewText.view || 'VIEW'} ${index + 1}`;
    if (raw === '3/4 VIEW') return overviewText.three_quarter_view || normalize.translate(raw, displayValues);
    return normalize.translate(raw, displayValues);
  };

  normalize.hasFullConfig = (config) => !!(config.colors && Object.keys(config.colors).length);

  normalize.colorOptionGroups = (config, colors = []) => {
    const validColors = new Set(colors.map((item) => normalize.itemKey(item)).filter(Boolean));
    return normalize.asArray(config.colorOptionGroups)
      .map((group) => ({
        key: String(group?.key || ''),
        label: String(group?.label || group?.title || normalize.titleCase(group?.key)),
        options: normalize.asArray(group?.options)
          .map((option) => ({
            key: String(option?.key || ''),
            label: String(option?.label || option?.display || normalize.titleCase(option?.key)),
            tone: option?.tone || option?.swatch || option?.hex || '',
            colors: normalize.asArray(option?.colors)
              .map(String)
              .filter((colorKey) => validColors.has(colorKey))
          }))
          .filter((option) => option.key && option.colors.length)
      }))
      .filter((group) => group.key && group.options.length);
  };

  normalize.activeColorGroupOption = (group, colorKey) =>
    normalize.asArray(group?.options).find((option) => normalize.asArray(option?.colors).includes(colorKey)) || null;

  normalize.resolveGroupedColor = (groups, currentColor, groupKey, optionKey) => {
    const targetGroup = normalize.asArray(groups).find((group) => group.key === groupKey);
    const targetOption = normalize.asArray(targetGroup?.options).find((option) => option.key === optionKey);
    if (!targetOption) return '';

    let candidates = normalize.asArray(targetOption.colors);
    for (const group of normalize.asArray(groups)) {
      if (group.key === groupKey) continue;
      const active = normalize.activeColorGroupOption(group, currentColor);
      if (!active) continue;
      const allowed = new Set(normalize.asArray(active.colors));
      candidates = candidates.filter((colorKey) => allowed.has(colorKey));
    }
    return candidates[0] || '';
  };

  normalize.detailRole = (item) => {
    const text = `${item?.role || ''} ${item?.label || ''} ${item?.display || ''} ${item?.alt || ''} ${item?.key || ''}`.toLowerCase();
    if (/interior|inside/.test(text)) return 'interior';
    if (/liner|padding/.test(text)) return 'liner';
    if (/\beps\b/.test(text)) return 'eps';
    if (/exploded|explosion/.test(text)) return 'explodedStructure';
    if (/shell/.test(text) && /structure|layer/.test(text)) return 'shellStructure';
    if (/vent/.test(text)) return 'ventilation';
    if (/visor/.test(text)) return 'visorSystem';
    if (/buckle|strap|retention/.test(text)) return 'buckle';
    if (/mechanism|hinge|flip/.test(text)) return 'mechanism';
    if (/certification|cert|mark/.test(text)) return 'certificationMark';
    if (/packaging|package|carton/.test(text)) return 'packaging';
    return '';
  };

  normalize.buildLists = ({ config, productMedia, dataset, hasFullConfig }) => {
    const fallbackViews = (productMedia || [])
      .filter((media) => !normalize.detailRole(media))
      .map((media, index) => ({
        key: `view${index + 1}`,
        label: normalize.viewLabel(media, index),
        image: media.image,
        alt: media.alt
      }));
    const fallbackColors = normalize.tokens(dataset.fallbackColors).map((name, index) => ({
      key: normalize.slug(name) || `color${index}`,
      label: name,
      tone: /custom/i.test(name)
        ? 'conic-gradient(#176df5,#62c4ff,#f5cd52,#ee6c66,#176df5)'
        : index
          ? '#7d8791'
          : '#eef2f6'
    }));
    const fallbackVisors = dataset.hasVisor === 'true'
      ? normalize.tokens(dataset.fallbackVisors).map((name, index) => ({
          key: normalize.slug(name) || `visor${index}`,
          label: name,
          tone: /clear/i.test(name)
            ? 'linear-gradient(135deg,#fff,#dce4ec,#fff)'
            : /smoke|black/i.test(name)
              ? 'linear-gradient(135deg,#242a31,#747d87)'
              : /silver/i.test(name)
                ? 'linear-gradient(135deg,#fff,#929da9,#eef3f7)'
                : 'linear-gradient(135deg,#8ae0ff,#7866ff,#ff8db8)'
        }))
      : [];
    const structures = hasFullConfig
      ? (normalize.collection(config.structures).length
          ? normalize.collection(config.structures)
          : normalize.asArray(config.capabilities?.structures).map((value) => ({
              key: value,
              label: { closed: 'CLOSED', visorUp: 'VISOR UP', flipUp: 'FLIP-UP' }[value] || normalize.titleCase(value)
            })))
      : [];
    const configuredViews = normalize.collection(config.views)
      .filter((item) => !normalize.detailRole(item));
    const capabilityViews = normalize.asArray(config.capabilities?.views)
      .filter((value) => !normalize.detailRole({ key: value }))
      .map((value) => ({
        key: value,
        label: {
          front: 'FRONT',
          threeQuarter: '3/4 VIEW',
          side: 'SIDE',
          rearThreeQuarter: 'REAR 3/4',
          rear: 'REAR'
        }[value] || normalize.titleCase(value)
      }));
    const views = hasFullConfig
      ? (configuredViews.length ? configuredViews : capabilityViews)
      : fallbackViews;
    return {
      colors: hasFullConfig ? normalize.collection(config.colors) : fallbackColors,
      visors: hasFullConfig ? normalize.collection(config.visors) : fallbackVisors,
      logos: hasFullConfig ? normalize.collection(config.logos) : [],
      structures,
      views,
      fallbackViews
    };
  };

  normalize.initialState = (lists, defaults) => ({
    color: defaults.color || normalize.itemKey(lists.colors[0]),
    visor: defaults.visor || normalize.itemKey(lists.visors[0]),
    logo: defaults.logo || normalize.itemKey(lists.logos[0]),
    structure: defaults.structure || normalize.itemKey(lists.structures[0]),
    view: defaults.view || normalize.itemKey(lists.views[0])
  });

  api.normalize = normalize;
})(typeof window !== 'undefined' ? window : globalThis);
